import { useModal, Modal, ModalContent, ModalButtons } from '@/components/Modal';
import { Button, CheckboxGroup, CheckboxOption } from '@/form/FormComponents';
import { FormProvider, FormSubmitProvider } from '@/form/FormProvider';
import { publicAction } from '@/data/loaders';
import { usePrivacyMode } from '@/systems/Authentication';

function ExportPDFModalContent({ closeModal, setLoading, context, openNothingToDownloadModal }) {
  const privacyMode = usePrivacyMode();

  const { scenario_data, session_id, assessment_id, course_id } = context;

  const has_assessment_and_plan = scenario_data?.assessment_and_plan;
  const has_diagnoses = scenario_data?.diagnose;
  const has_questions = scenario_data?.questions?.length > 0;

  function handleExport({ formatted_data, focusError }) {
    setLoading(true);

    publicAction('exportSessions', {
      session_id,
      assessment_id,
      course_id,
      sections: scenario_data ? [...formatted_data.sections, !has_assessment_and_plan && 'assessment_and_plan', !has_diagnoses && 'diagnoses', !has_questions && 'questions'].filter(a => a) : [...formatted_data.sections, ...(formatted_data.sections.includes('transcript') ? ['diagnoses', 'assessment_and_plan', 'questions'] : [])],
      anonymize: !!privacyMode,
    }, { download_file: true }).then(function () {
      closeModal();
    }).catch(function (error) {
      if (error.status === 404) {
        closeModal();
        openNothingToDownloadModal();
      } else {
        console.error(error);
        setLoading(false);
        focusError({ name: 'sections', error: 'Something went wrong, please try again.' });
      }
    });
  }

  return (
    <Modal title={assessment_id ? "Download all completed submissions" : (course_id && !assessment_id ? "Download all my submissions" : "Download as PDF")}>
      <FormProvider onSubmit={handleExport}>
        <FormSubmitProvider>
          {({ handleSubmit }) => (
            <>
              <ModalContent>
                <CheckboxGroup name="sections" title="Sections to include" default_value={['feedback', 'transcript', has_diagnoses && 'diagnoses', has_assessment_and_plan && 'assessment_and_plan', has_questions && 'questions'].filter(a => a)} required="Please select at least one section">
                  <CheckboxOption name={"feedback"} text={"Feedback"} />
                  <CheckboxOption name={"transcript"} text={"Transcript"} />
                  {has_diagnoses && <CheckboxOption name={"diagnoses"} text={"Diagnoses"} />}
                  {has_assessment_and_plan && <CheckboxOption name={"assessment_and_plan"} text={"Assessment and Plan"} />}
                  {has_questions && <CheckboxOption name={"questions"} text={"Follow-up Questions"} />}
                </CheckboxGroup>
              </ModalContent>
              <ModalButtons>
                <Button text="Cancel" className="hollow" onClick={closeModal} />
                <Button text="Download" className="primary" onClick={handleSubmit} />
              </ModalButtons>
            </>
          )}
        </FormSubmitProvider>
      </FormProvider>
    </Modal>
  );
}

export function useExportPDFModal() {
  const [openNothingToDownloadModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="There's nothing to download">
        <ModalContent>
          {"There aren't any completed submissions to download."}
        </ModalContent>
        <ModalButtons>
          <Button text="Got it" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  }, []);

  return useModal(function ({ context, closeModal, setLoading }) {
    return <ExportPDFModalContent closeModal={closeModal} setLoading={setLoading} context={context} openNothingToDownloadModal={openNothingToDownloadModal} />;
  });
}
